import { useMutation } from '@apollo/client'
import clsx from 'clsx'
import { FormikErrors, useFormik } from 'formik'
import { useState, useEffect, useContext } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { UPDATE_USER } from '../graphql/mutation'
import { GET_USER_BY_ID } from '../graphql/query'
import * as Yup from 'yup'
import UserContext from '../context/UserContext'

interface IProps {
  closeProfileModal?: () => void
  loading: any
  error: any
  data: any
}

export const OnboardingSchema = Yup.object().shape({
  Fullname: Yup.string()
    .min(2, 'Minimum 2 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Full name is required'),
  ContactNumber: Yup.string()
    .min(10, 'Minimum 10 numbers')
    .max(10, 'Maximum 10 numbers')
    .required('Contact Number is required'),
  Designation: Yup.string()
    .min(2, 'Minimum 4 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Designation is required')
})

const EditProfileModal: React.FC<IProps> = ({ closeProfileModal, loading, error, data }) => {
  const [isLoading, setIsLoading] = useState(false)
  const { currentUser } = useContext(UserContext)
  const [updateUser] = useMutation(UPDATE_USER, {
    refetchQueries: [
      {
        query: GET_USER_BY_ID,
        context: {
          headers: {
            'access-control-allow-methods': 'Profile'
          }
        }
      }
    ],
    context: {
      headers: {
        'access-control-allow-methods': 'Profile'
      }
    }
  })
  const navigate = useNavigate()

  const [selectedOptions, setSelectedOptions] = useState({
    Department: '',
    Role: ''
  })

  const handleDropdownSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const name = e.target.name
    const value = e.target.value

    setSelectedOptions((prevValue) => ({
      ...prevValue,
      [name]: value
    }))
  }

  // const { loading, error, data } = useQuery(GET_USER_BY_ID)

  useEffect(() => {
    if (!!data) {
      setSelectedOptions((prevValue) => ({
        ...prevValue,
        Department: data.getUserById.Department,
        Role: data.getUserById.UserRole[0].roleId
      }))
    }
  }, [data])
  console.log(selectedOptions)
  const initialValues = {
    Fullname: !!data && !loading && !error && data.getUserById.name ? data.getUserById.name : '',

    ContactNumber:
      !!data && !loading && !error && data.getUserById.contactNumber
        ? data.getUserById.contactNumber
        : 0,
    Designation:
      !!data && !loading && !error && data.getUserById.Designation
        ? data.getUserById.Designation
        : ''
  }

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: OnboardingSchema,
    onSubmit: (values, { setSubmitting }) => {
      const user = data.getUserById
      setIsLoading(true)
      updateUser({
        variables: {
          input: {
            id: parseInt(currentUser.id),
            name: values.Fullname,
            contactNumber: values.ContactNumber,
            Designation: values.Designation,
            Department: selectedOptions.Department,
            UserRoles: [{ roleId: selectedOptions.Role }],
            ...(!closeProfileModal && user.UserRole[0].roleId !== 1 && { onboardingDone: true })
          }
        }
      })
        .then((res: any) => {
          setIsLoading(false)
          if (res.data.updateUser.status >= 200 && res.data.updateUser.status < 300) {
            // toast.success(res.data.updateUser.message)
            !!closeProfileModal && closeProfileModal()
            !closeProfileModal &&
              user.UserRole[0].roleId === 1 &&
              navigate('/onboarding/business-details')
            !closeProfileModal && user.UserRole[0].roleId !== 1 && navigate('/dashboard')
          } else {
            toast.error(res.data.updateUser.message)
            setSubmitting(false)
            !!closeProfileModal && closeProfileModal()
          }
        })
        .catch((err) => {
          setIsLoading(false)
          setSubmitting(false)
          toast.error('Sorry! Unexpected Error.')
          console.error(err)
          !!closeProfileModal && closeProfileModal()
        })
    }
  })

  if (loading) {
    return <h1>Loading...</h1>
  }

  if (error) {
    return <p>error</p>
  }

  const getErrorMessage = (error: string | string[] | FormikErrors<any> | FormikErrors<any>[]) => {
    if (typeof error === 'string') {
      return error
    } else if (Array.isArray(error)) {
      return error.join(', ')
    } else {
      return JSON.stringify(error)
    }
  }

  return (
    <div className='w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_signup_form'
        onSubmit={formik.handleSubmit}
      >
        {!closeProfileModal && (
          <div className='mb-10 text-center'>
            {/* begin::Title */}
            <h2 className='text-dark mb-4'>Hey. Thanks for choosing Botgo!</h2>
            <p className='text-muted fs-5'>Tell us a little bit about yourself.</p>
          </div>
        )}
        {formik.status && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}

        <div className='fv-row mb-7'>
          <label className='form-label fw-bolder text-dark fs-6 required'>Full Name :</label>
          <input
            placeholder='Full name'
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('Fullname')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {
                'is-invalid': formik.touched.Fullname && formik.errors.Fullname
              },
              {
                'is-valid': formik.touched.Fullname && !formik.errors.Fullname
              }
            )}
          />
          {formik.touched.Fullname && formik.errors.Fullname && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{getErrorMessage(formik.errors.Fullname)}</span>
              </div>
            </div>
          )}
        </div>

        <div className='fv-row mb-7'>
          <label className='form-label fw-bolder text-dark fs-6 required'>Contact Number :</label>
          <input
            placeholder='Contact Number'
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('ContactNumber')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {
                'is-invalid': formik.touched.ContactNumber && formik.errors.ContactNumber
              },
              {
                'is-valid': formik.touched.ContactNumber && !formik.errors.ContactNumber
              }
            )}
          />
          {formik.touched.ContactNumber && formik.errors.ContactNumber && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{getErrorMessage(formik.errors.ContactNumber)}</span>
              </div>
            </div>
          )}
        </div>

        <div className='fv-row mb-7'>
          <label className='form-label fw-bolder text-dark fs-6'>Designation :</label>
          <input
            placeholder='Designation'
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('Designation')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              { 'is-invalid': formik.touched.Designation && formik.errors.Designation },
              {
                'is-valid': formik.touched.Designation && !formik.errors.Designation
              }
            )}
          />
          {formik.touched.Designation && formik.errors.Designation && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{getErrorMessage(formik.errors.Designation)}</span>
              </div>
            </div>
          )}
        </div>
        <div className='mb-10 fv-row'>
          <label className='form-label fw-bolder text-dark fs-6 required'>Department :</label>
          <div className='position-relative mb-3'>
            <select
              className=' form-select form-select-solid form-select '
              value={selectedOptions.Department}
              onChange={handleDropdownSelect}
              name='Department'
            >
              <option key='blankKey' hidden>
                Select
              </option>
              <option value='Customer Service'>Customer Service</option>
              <option value='IT ,Engineering & Developement'>IT ,Engineering & Developement</option>
              <option value='Project Management & Innovation'>
                Project Management & Innovation
              </option>
              <option value='Marketing'>Marketing</option>
              <option value='Sales'>Sales</option>
              <option value='HR'>HR</option>
              <option value='Executive Management'>Executive Management</option>
              <option value='Other'>Other</option>
            </select>
          </div>
        </div>

        <div className='text-center'>
          <button
            type='submit'
            id='kt_sign_up_submit'
            className='btn btn-lg btn-primary w-100 mb-5'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!isLoading && <span className='indicator-label'>Submit</span>}
            {isLoading && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>

          {!closeProfileModal && (
            <Link to='/bots/all-bots'>
              <button
                type='button'
                id='kt_login_signup_form_cancel_button'
                className='btn btn-lg btn-secondary w-100 mb-5'
              >
                Do it later
              </button>
            </Link>
          )}

          {!!closeProfileModal && (
            <button
              type='button'
              id='kt_login_signup_form_cancel_button'
              className='btn btn-lg btn-secondary w-100 mb-5'
              onClick={closeProfileModal}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

export default EditProfileModal
